import { z } from 'zod';
import { isMongoId } from '../../helpers';
import { RevaluationType } from 'further-types/lp/revaluation';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { ActionTypes } from 'further-types/lp/action';
import { IndividualTransaction } from '../transaction';
import CalendarDay from '../../../utils/calendar-day';

// Augment IndividualTransaction with investmentId for revaluation context
export const ExtendendIndividualTransaction = IndividualTransaction.extend({
  investmentId: z.string().refine(isMongoId(), {
    message: 'Invalid investment ID',
  }),
  actionType: z.nativeEnum(ActionTypes),
});

const BaseRevaluationSchema = z.object({
  holdingClassId: z
    .string()
    .refine(isMongoId({ optional: true }), {
      message: 'Invalid holding class ID',
    })
    .optional(),
  holdingClassName: z.string(),
  ledgerDate: z.date({
    message: 'Invalid date format',
  }),
  currentHoldValue: z
    .number({
      required_error: 'Current hold value is required',
      invalid_type_error: 'Current hold value must be a number',
    })
    .optional(),
  unitPriceTcy: z.number({
    required_error: 'Unit price is required',
    invalid_type_error: 'Unit price must be a number',
  }),
  currentNumberOfUnits: z.number(),
  assetId: z.string().refine(isMongoId({ optional: true }), {
    message: 'Invalid asset ID',
  }),
  currency: z.nativeEnum(CurrencyCode),
  isRemoved: z.boolean().default(false).optional(),
  isNew: z.boolean().default(false),
});

const StandardRevaluationSchema = BaseRevaluationSchema.extend({
  type: z.literal(RevaluationType.Revaluation),
  revaluationId: z.string().refine(isMongoId(), {
    message: 'Invalid revaluation ID',
  }),
});

const InitialInvestmentRevaluationSchema = BaseRevaluationSchema.extend({
  type: z.literal(RevaluationType.InitialInvestment),
  revaluationId: z.string().refine(isMongoId(), {
    message: 'Invalid investment ID',
  }),
});

export const RevaluationSchema = z.discriminatedUnion('type', [
  StandardRevaluationSchema,
  InitialInvestmentRevaluationSchema,
]);

export const NewStandardRevaluationSchema = StandardRevaluationSchema.extend({
  type: z.enum([RevaluationType.Revaluation]),
  revaluationId: z
    .string()
    .refine(isMongoId({ optional: true }), {
      message: 'Invalid revaluation ID',
    })
    .optional(),
  isRemoved: z.boolean(),
});

export const InvestmentHistorySchema = z.object({
  holdingClassId: z.string(),
  date: z.instanceof(CalendarDay),
  name: z.string(),
  currency: z.nativeEnum(CurrencyCode),
  investmentAmountTcy: z.number(),
  totalNumberOfUnits: z.number(),
  currentNumberOfUnits: z.number(),
  unitPriceTcy: z.number(),
  latestRevaluationDate: z.instanceof(CalendarDay).optional(),
  latestRevaluationAmount: z.number().optional(),
  currentUnrealisedValue: z.number(),
  currentMOIC: z.string(),
});

export const RevaluationFormSchema = z.object({
  revaluations: z.array(RevaluationSchema).optional(),
  newRevaluations: z.array(NewStandardRevaluationSchema),
  assetCurrency: z.nativeEnum(CurrencyCode),
  investmentHistory: z.array(InvestmentHistorySchema).optional(),
  revaluationTransactions: z
    .record(z.string(), z.array(ExtendendIndividualTransaction))
    .optional(),
  revaluationCurrencyRates: z
    .record(
      z.string(),
      z
        .object({
          fetchedRate: z.number().optional(),
          fetchedSource: z.nativeEnum(FxDataFeed).optional(),
          overrideRate: z.number().optional(),
          overrideSource: z.string().optional(),
        })
        .optional(),
    )
    .optional(),
  currentUnitPrice: z.number().optional(),
  currentHoldValue: z.number().optional(),
  currentRevaluationDate: z.date().optional(),
});

export const CreateRevaluationSchema = z.object({
  revaluations: z.array(BaseRevaluationSchema),
  revaluationTransactions: z
    .record(z.string(), z.array(ExtendendIndividualTransaction))
    .optional(),
  revaluationCurrencyRates: z.record(
    z.string(),
    z
      .object({
        fetchedRate: z.number().optional(),
        fetchedSource: z.nativeEnum(FxDataFeed).optional(),
        overrideRate: z.number().optional(),
        overrideSource: z.string().optional(),
      })
      .optional(),
  ),
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
});

export const BulkCreateRevaluationSchema = z.object({
  items: z.array(CreateRevaluationSchema),
});

export type BulkCreateRevaluationType = z.infer<typeof BulkCreateRevaluationSchema>;

export const CalculateUnrealisedGainsSchema = z.object({
  assetId: z.string().refine(isMongoId(), {
    message: 'Invalid asset ID',
  }),
  vehicleId: z.string().refine(isMongoId(), {
    message: 'Invalid vehicle ID',
  }),
  holdingClassId: z.string().refine(isMongoId(), {
    message: 'Invalid holding class ID',
  }),
  revaluationDate: z
    .instanceof(CalendarDay)
    .refine((date) => date instanceof CalendarDay, {
      message: 'Please select a valid revaluation date',
    }),
  newUnitPriceTcy: z.number(),
  currencyRate: z
    .object({
      fetchedRate: z.number().optional(),
      fetchedSource: z.nativeEnum(FxDataFeed).optional(),
      overrideRate: z.number().optional(),
      overrideSource: z.string().optional(),
    })
    .optional(),
});

export type CalculateUnrealisedGainsType = z.infer<
  typeof CalculateUnrealisedGainsSchema
>;

export const GetRevaluationTransactionsSchema =
  CalculateUnrealisedGainsSchema.extend({
    transactionCurrency: z.nativeEnum(CurrencyCode),
  });

export type GetRevaluationTransactionsType = z.infer<
  typeof GetRevaluationTransactionsSchema
>;

export type RevaluationFormType = z.infer<typeof RevaluationFormSchema>;
